import React, { useRef, useState, useEffect } from "react";
import { Animated, Dimensions, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../theme";
import { useToast } from "../../components/LhToastContainer";
import { AppDispatch, RootState } from "../../store";
import { setHomepageBanners, setSelectedTicketId, setTicketDetailSource, HomepageBannerNotif } from "../../store/slices/ticketingPortalSlice";
import styles from "./NotificationsCommsScreen.style";
import mobileStyles, { NOTIF_BORDER_COLOR } from "./NotificationsCommsScreen.mobile.style";
import useNotificationsComms from "./hooks/useNotificationsComms";
import type { NotificationType } from "./hooks/useNotificationsComms";
import NotificationList from "./components/NotificationList";
import CreateNotificationModal, { CreateNotificationFormState } from "./components/CreateNotificationModal";
import useTicketingAccess from "./hooks/useTicketingAccess";
import LHLoader from "../../components/LHLoader";
import { formatDateWithTimezone, formatLocalDateTimeWithTimezone } from "./util/dateFormatter";

type Props = {
  onNavigate: (screen: string) => void;
};

const NotificationsCommsScreen: React.FC<Props> = ({ onNavigate }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { canViewSustainFeatures, hasWriteAccess, isReadOnly, accessLoading } = useTicketingAccess();

  // Route guard – redirect users who cannot view sustain features
  useEffect(() => {
    if (!accessLoading && !canViewSustainFeatures) {
      onNavigate("Ticketing Portal");
    }
  }, [accessLoading, canViewSustainFeatures, onNavigate]);

  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((state: RootState) => (state as any).auth?.user);
  const userId: string = authUser?.email ?? authUser?.username ?? "";
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editNotif, setEditNotif] = useState<{
    id: string;
    initialValues: CreateNotificationFormState;
  } | null>(null);
  // Stores the original FormState for every notification created/edited via modal
  const formSnapshotsRef = useRef<Record<string, CreateNotificationFormState>>({});
  const {
    notifications,
    activeTab,
    tabCounts,
    tabs,
    setActiveTab,
    setReadFilter,
    toggleReadStatus,
    deleteNotification,
    sendToApi,
    updateOnApi,
  } = useNotificationsComms();

  // Sync active homepage banners to Redux so TicketingPortalScreen can render them
  useEffect(() => {
    const banners: HomepageBannerNotif[] = notifications
      .filter((n) => n.statusTab === "active" && n.isHomepageBanner)
      .map((n) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        linkedTicket: n.linkedTicket,
        includeActionButton: n.includeActionButton,
        endDate: n.endDate,
      }));
    dispatch(setHomepageBanners(banners));
  }, [dispatch, notifications]);

  const handleDelete = (id: string) => {
    deleteNotification(id);
    toast.show("Notification deleted", "info");
  };

  const closeModal = () => {
    setCreateModalOpen(false);
    setEditNotif(null);
  };

  const handleCreateNotification = () => {
    setEditNotif(null);
    setCreateModalOpen(true);
  };

  // Derive a best-effort FormState from a PortalNotification that has no snapshot.
  // (Applies only to seed/mock notifications not created via the modal.)
  const deriveFormFromNotif = (
    n: typeof notifications[number]
  ): CreateNotificationFormState => {
    // Map backend audienceTargetType values to form AudienceType labels
    const audienceTypeMap: Record<string, string> = {
      "ALL_USERS":       "All Users",
      "BY_REGION":       "By Region",
      "BY_MARKET":       "By Market",
      "BY_LOCATION":     "By Location",
      "BY_ROLE":         "By Role",
      "BY_TARGET_SYSTEM": "By Target System",
    };

    // Parse comma-separated audienceTargetType into an array of AudienceType labels
    const rawTypes = n.audienceTargetType
      ? n.audienceTargetType.split(",").map((s) => s.trim())
      : [];
    const audienceTypes = rawTypes
      .map((t) => audienceTypeMap[t])
      .filter(Boolean) as any[];
    if (audienceTypes.length === 0) audienceTypes.push("All Users");

    const targetValues = n.targetValues || { regions: [], markets: [], locations: [], roles: [], systems: [] };

    return {
      notifType:
        n.type === "incident" ? "Active Incident"
        : n.type === "maint"  ? "Scheduled Maintenance"
        : "General Announcement",
      priority: "Standard",
      title: n.title,
      message: n.message,
      linkedTicket: n.linkedTicket ?? "",
      displayUntilClosed: n.displayUntilClosed ?? false,
      includeActionButton: n.includeActionButton ?? false,
      audienceTypes,
      regions:       Array.isArray(targetValues.regions)   ? targetValues.regions   : [],
      markets:       Array.isArray(targetValues.markets)   ? targetValues.markets   : [],
      locations:     Array.isArray(targetValues.locations) ? targetValues.locations : [],
      roles:         Array.isArray(targetValues.roles)     ? targetValues.roles     : [],
      targetSystems: Array.isArray(targetValues.systems)   ? targetValues.systems   : [],
      sendVia: {
        inApp:  n.isHomepageBanner ?? false,
        email:  n.triggerEmailNotification ?? false,
        teams:  n.triggerTeamsNotification ?? false,
      },
      startDate: n.startDate ?? "",
      endDate:   n.endDate   ?? "",
    };
  };

  const handleEditNotification = (id: string) => {
    const notif = notifications.find((n) => n.id === id);
    if (!notif) return;
    const snapshot =
      formSnapshotsRef.current[id] ?? deriveFormFromNotif(notif);
    setEditNotif({ id, initialValues: snapshot });
    setCreateModalOpen(true);
  };

  const handlePressLinkedTicket = (ticketId: string) => {
    toast.show(`Opening ticket ${ticketId}…`, "info");
    dispatch(setSelectedTicketId(ticketId));
    dispatch(setTicketDetailSource("Notifications & Comms"));
    onNavigate("Ticket Detail");
  };

  // ── Shared audience label builder ──────────────────────────────────────────
  const getAudienceLabel = (f: CreateNotificationFormState): string => {
    const types: string[] = Array.isArray((f as any).audienceTypes)
      ? (f as any).audienceTypes
      : [(f as any).audienceType ?? "All Users"];
    if (types.includes("All Users")) return "All Users";
    const parts: string[] = [];
    if (types.includes("By Region")        && f.regions.length > 0)
      parts.push(`By Region · ${f.regions.length} selected`);
    if (types.includes("By Market")        && f.markets.length > 0)
      parts.push(`By Market · ${f.markets.length} selected`);
    if (types.includes("By Location")      && f.locations.length > 0)
      parts.push(f.locations.length > 2 ? `By Location · ${f.locations.length} locations` : f.locations.join(" · "));
    if (types.includes("By Role")          && f.roles.length > 0)
      parts.push(`By Role · ${f.roles.join(", ")}`);
    if (types.includes("By Target System") && f.targetSystems.length > 0)
      parts.push(`By System · ${f.targetSystems.join(", ")}`);
    return parts.length > 0 ? parts.join(" | ") : types.join(", ") || "All Users";
  };

  // ── Modal handlers (create + edit) ────────────────────────────────────────
  const handleModalDraft = async (formData: CreateNotificationFormState) => {
    const typeMap: Record<string, NotificationType> = {
      "Scheduled Maintenance": "maint",
      "Active Incident": "incident",
      "General Announcement": "general",
    };
    const now = new Date();
    const draftSavedFormatted = formatDateWithTimezone(now.toISOString());
    const notifFields = {
      type: (typeMap[formData.notifType] ?? "general") as NotificationType,
      statusTab: "draft" as const,
      readStatus: "unread" as const,
      title: formData.title || "(Untitled draft)",
      message: formData.message || "",
      locations: getAudienceLabel(formData),
      startedAt: `Draft saved ${draftSavedFormatted}`,
      linkedTicket: formData.linkedTicket || undefined,
      stats: { notified: 0 },
      isHomepageBanner: formData.sendVia.inApp,
      draft: true,
    };
    
    let saved = false;

    if (editNotif) {
      // Update existing draft via API
      const success = await updateOnApi(editNotif.id, notifFields, formData, userId, true);
      if (success) {
        formSnapshotsRef.current[editNotif.id] = formData;
        saved = true;
      }
    } else {
      // Create new draft via API
      const newId = await sendToApi(notifFields, formData, userId, true);
      if (newId) {
        formSnapshotsRef.current[newId] = formData;
        saved = true;
      }
    }

    if (!saved) {
      closeModal();
      toast.show(editNotif ? "Failed to update draft" : "Failed to save draft", "error");
      return;
    }

    closeModal();
    setReadFilter("all");
    setActiveTab("draft");
    toast.show(editNotif ? "Draft updated" : "Draft saved", "info");
  };

  const handleModalSend = async (formData: CreateNotificationFormState) => {
    const typeMap: Record<string, NotificationType> = {
      "Scheduled Maintenance": "maint",
      "Active Incident": "incident",
      "General Announcement": "general",
    };
    const now = new Date();
    // Use the live count from the audience/count API, stamped onto formData just before submit.
    const recipientCount = formData.estimatedUserCount ?? 0;
    
    // Format the start date with timezone for display
    const deliveryStartFormatted = formData.displayUntilClosed
      ? formatDateWithTimezone(new Date().toISOString())
      : formData.startDate
        ? formatLocalDateTimeWithTimezone(formData.startDate)
        : formatDateWithTimezone(new Date().toISOString());
    
    const isFutureStart = !formData.displayUntilClosed && !!formData.startDate && new Date(formData.startDate) > now;
    const isEndPast = !formData.displayUntilClosed && !!formData.endDate && new Date(formData.endDate) <= now;
    // "active" = started and end date still in future (or no end); "scheduled" = future start; "sent" = fully past
    const statusTab: "active" | "scheduled" | "sent" = isFutureStart
      ? "scheduled"
      : isEndPast
        ? "sent"
        : "active";
    const notifFields = {
      type: (typeMap[formData.notifType] ?? "general") as NotificationType,
      statusTab: statusTab as "active" | "scheduled" | "sent",
      readStatus: "read" as const,
      title: formData.title || "(No title)",
      message: formData.message || "",
      locations: getAudienceLabel(formData),
      startedAt: isFutureStart ? `Scheduled · starts ${deliveryStartFormatted}` : `Sent ${deliveryStartFormatted}`,
      linkedTicket: formData.linkedTicket || undefined,
      stats: { notified: recipientCount, seen: 0 },
      isHomepageBanner: formData.sendVia.inApp,
      includeActionButton: formData.includeActionButton,
      endDate: !formData.displayUntilClosed && formData.endDate ? formData.endDate : undefined,
      draft: false,
    };
    let sent = false;

    if (editNotif) {
      const success = await updateOnApi(editNotif.id, notifFields, formData, userId, false);
      if (success) {
        formSnapshotsRef.current[editNotif.id] = formData;
        sent = true;
      }
    } else {
      const newId = await sendToApi(notifFields, formData, userId, false);
      if (newId) {
        formSnapshotsRef.current[newId] = formData;
        sent = true;
      }
    }

    if (!sent) {
      closeModal();
      toast.show(editNotif ? "Failed to update notification" : "Failed to send notification", "error");
      return;
    }

    closeModal();
    setReadFilter("all");
    setActiveTab(statusTab);
    toast.show(
      editNotif
        ? "Notification updated."
        : isFutureStart
          ? `Notification scheduled. Delivery begins at ${deliveryStartFormatted}.`
          : `Notification sent to ${recipientCount.toLocaleString()} recipients.`,
      "success"
    );
  };

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  /* ── Mobile-only state (must be top-level, not inside if) ─── */
  const [mobileActionSheet, setMobileActionSheet] = useState<{ notifId: string; title: string } | null>(null);
  const [deactivatedIds, setDeactivatedIds] = useState<Set<string>>(new Set());
  const [mobileSnackbar, setMobileSnackbar] = useState<string | null>(null);
  const snackbarTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snackbarAnim = useRef(new Animated.Value(-60)).current;

  const showMobileSnackbar = (msg: string) => {
    if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
    snackbarAnim.setValue(-60);
    setMobileSnackbar(msg);
    Animated.timing(snackbarAnim, { toValue: 0, duration: 250, useNativeDriver: true }).start();
    snackbarTimer.current = setTimeout(() => {
      Animated.timing(snackbarAnim, { toValue: -60, duration: 200, useNativeDriver: true }).start(() =>
        setMobileSnackbar(null)
      );
    }, 2500);
  };

  /* ── Helpers for mobile cards ─────────────────────────────── */
  const TYPE_LABEL: Record<NotificationType, string> = {
    incident: "Active Incident",
    maint: "Scheduled Maintenance",
    general: "General Announcement",
  };

  const TYPE_BADGE = (type: NotificationType) => {
    switch (type) {
      case "incident":
        return { bg: mobileStyles.badgeIncident, text: mobileStyles.badgeIncidentText, dot: mobileStyles.badgeIncidentDot };
      case "maint":
        return { bg: mobileStyles.badgeMaint, text: mobileStyles.badgeMaintText, dot: mobileStyles.badgeMaintDot };
      default:
        return { bg: mobileStyles.badgeGeneral, text: mobileStyles.badgeGeneralText, dot: mobileStyles.badgeGeneralDot };
    }
  };

  // Prevent unauthorized content from flashing while access resolves
  if (accessLoading || !canViewSustainFeatures) {
    return <LHLoader />;
  }

  /* ── iPhone mobile layout ─────────────────────────────────── */
  if (isiPhone) {
    const SECTIONS: { key: string; label: string }[] = [
      { key: "active",    label: "ACTIVE NOTIFICATIONS" },
      { key: "scheduled", label: "SCHEDULED" },
      { key: "sent",      label: "SENT" },
      { key: "draft",     label: "DRAFTS" },
    ];

    const STATUS_BADGE: Record<string, { label: string; color: string; bg: string; dot: string; darkColor?: string; darkBg?: string }> = {
      active:    { label: "ACTIVE",       color: "#027C06", bg: "#DDF9DA", dot: "#027C06", darkColor: "#4ade80", darkBg: "#14301a" },
      scheduled: { label: "SCHEDULED",    color: "#034895", bg: "#DAEEFA", dot: "#034895", darkColor: "#82b4ff", darkBg: "#0d2a44" },
      sent:      { label: "SENT",         color: "#585858", bg: "#EBEBEB", dot: "#585858", darkColor: "#9ca3af", darkBg: "#2a2a2a" },
      draft:     { label: "DRAFT",        color: "#818181", bg: "#F3F4F6", dot: "#9CA3AF", darkColor: "#9ca3af", darkBg: "#262626" },
      deactivated: { label: "DEACTIVATED", color: "#6b7280", bg: "#F3F4F6", dot: "#9CA3AF", darkColor: "#6b7280", darkBg: "#262626" },
    };

    const renderCard = (notif: (typeof notifications)[0]) => {
      const isDeactivated = deactivatedIds.has(notif.id);
      const borderColor = isDeactivated ? "#D1D5DB" : (NOTIF_BORDER_COLOR[notif.type] ?? "#838383");
      const badge = TYPE_BADGE(notif.type);
      const sbKey = isDeactivated ? "deactivated" : notif.statusTab;
      const sb = STATUS_BADGE[sbKey];

      return (
        <View key={notif.id} style={[mobileStyles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }, isDeactivated && mbLocal.cardDeactivated]}>
          <View style={[mobileStyles.cardLeftBorder, { backgroundColor: borderColor }]} />
          <View style={mobileStyles.cardContent}>
            <View style={mobileStyles.cardTopRow}>
              <Text style={[mobileStyles.cardTitle, isDark && { color: colors.text.primary }, isDeactivated && mbLocal.dimText]}>{notif.title}</Text>
              <Pressable
                style={[mobileStyles.moreBtn, isDeactivated && mbLocal.dimBtn]}
                disabled={isDeactivated}
                onPress={() => setMobileActionSheet({ notifId: notif.id, title: notif.title })}
              >
                <MaterialIcons name="more-vert" size={20} color={isDeactivated ? "#C0C0C0" : (isDark ? colors.text.secondary : "#585858")} />
              </Pressable>
            </View>
            <View style={mobileStyles.badgeRow}>
              <View style={[mobileStyles.badge, badge.bg, isDeactivated && mbLocal.dimBadge]}>
                <View style={[mobileStyles.badgeDot, badge.dot]} />
                <Text style={[mobileStyles.badgeText, badge.text, isDeactivated && mbLocal.dimText]}>{TYPE_LABEL[notif.type]}</Text>
              </View>
              {notif.isHomepageBanner && !isDeactivated ? (
                <View style={[mobileStyles.badge, mobileStyles.badgeAuto]}>
                  <Text style={[mobileStyles.badgeText, mobileStyles.badgeAutoText]}>Auto</Text>
                </View>
              ) : null}
            </View>
            <Text style={[mobileStyles.cardMessage, isDark && { color: colors.text.secondary }, isDeactivated && mbLocal.dimText]}>{notif.message}</Text>
            <View style={mobileStyles.cardMetaRow}>
              <View style={mobileStyles.cardMetaItem}>
                <MaterialIcons name="person-outline" size={13} color={isDeactivated ? "#C0C0C0" : (isDark ? colors.text.secondary : "#818181")} />
                <Text style={[mobileStyles.cardMetaText, isDark && { color: colors.text.secondary }, isDeactivated && mbLocal.dimText]}>{notif.locations}</Text>
              </View>
              <View style={mobileStyles.cardMetaItem}>
                <MaterialIcons name="schedule" size={13} color={isDeactivated ? "#C0C0C0" : (isDark ? colors.text.secondary : "#818181")} />
                <Text style={[mobileStyles.cardMetaText, isDark && { color: colors.text.secondary }, isDeactivated && mbLocal.dimText]}>{notif.startedAt}</Text>
              </View>
            </View>
            {notif.stats.notified > 0 ? (
              <>
                <View style={[mobileStyles.cardDivider, isDark && { backgroundColor: colors.border.divider }]} />
                <View style={mobileStyles.cardStatsRow}>
                  <View style={mobileStyles.cardStat}>
                    <Text style={[mobileStyles.cardStatNum, isDark && { color: colors.text.primary }, isDeactivated && mbLocal.dimText]}>{notif.stats.notified.toLocaleString()}</Text>
                    <Text style={[mobileStyles.cardStatLabel, isDark && { color: colors.text.secondary }, isDeactivated && mbLocal.dimText]}>Delivered</Text>
                  </View>
                  {/* Status badge */}
                  {sb ? (
                    <View style={[mbLocal.statusBadge, { backgroundColor: isDark ? (sb.darkBg ?? sb.bg) : sb.bg }]}>
                      <View style={[mbLocal.statusBadgeDot, { backgroundColor: isDark ? (sb.darkColor ?? sb.dot) : sb.dot }]} />
                      <Text style={[mbLocal.statusBadgeText, { color: isDark ? (sb.darkColor ?? sb.color) : sb.color }]}>{sb.label}</Text>
                    </View>
                  ) : null}
                </View>
              </>
            ) : null}
          </View>
        </View>
      );
    };

    /* active sheet notification */
    const actionNotif = mobileActionSheet
      ? notifications.find((n) => n.id === mobileActionSheet.notifId)
      : null;

    return (
      <View style={[mobileStyles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
        {/* ── Bottom Sheet Action Menu ─────────────────────────── */}
        <Modal
          visible={mobileActionSheet !== null}
          transparent
          animationType="slide"
          onRequestClose={() => setMobileActionSheet(null)}
        >
          <View style={mbLocal.sheetWrapper}>
            <Pressable style={StyleSheet.absoluteFillObject} onPress={() => setMobileActionSheet(null)} />
            <View style={[mbLocal.sheetContainer, isDark && { backgroundColor: colors.background.surface }]}>
            {/* Drag handle */}
            <View style={[mbLocal.sheetHandle, isDark && { backgroundColor: colors.border.default }]} />
            {/* Title */}
            <Text style={[mbLocal.sheetTitle, isDark && { color: colors.text.primary }]} numberOfLines={1}>{mobileActionSheet?.title ?? ""}</Text>
            <View style={[mbLocal.sheetDivider, isDark && { backgroundColor: colors.border.divider }]} />

            {/* Deactivate */}
            <Pressable
              style={({ pressed }) => [mbLocal.sheetItem, pressed && mbLocal.sheetItemPressed, isDark && pressed && { backgroundColor: colors.background.elevated }]}
              onPress={() => {
                if (mobileActionSheet) {
                  deleteNotification(mobileActionSheet.notifId);
                }
                setMobileActionSheet(null);
              }}
            >
              <View style={[mbLocal.sheetIconWrap, { backgroundColor: isDark ? "#0d2a44" : "#EFF6FF" }]}>
                <MaterialIcons name="block" size={22} color={isDark ? colors.brand.primary : "#034895"} />
              </View>
              <View style={mbLocal.sheetItemContent}>
                <Text style={[mbLocal.sheetItemTitle, isDark && { color: colors.text.primary }]}>Deactivate</Text>
                <Text style={[mbLocal.sheetItemSub, isDark && { color: colors.text.secondary }]}>Stop showing this notification to users</Text>
              </View>
            </Pressable>
            <View style={[mbLocal.sheetDivider, isDark && { backgroundColor: colors.border.divider }]} />

            {/* Edit */}
            <Pressable
              style={({ pressed }) => [mbLocal.sheetItem, pressed && mbLocal.sheetItemPressed, isDark && pressed && { backgroundColor: colors.background.elevated }]}
              onPress={() => {
                setMobileActionSheet(null);
                showMobileSnackbar("Edit is desktop only");
              }}
            >
              <View style={[mbLocal.sheetIconWrap, { backgroundColor: isDark ? "#0d2a44" : "#EFF6FF" }]}>
                <MaterialIcons name="edit" size={22} color={isDark ? colors.brand.primary : "#034895"} />
              </View>
              <View style={mbLocal.sheetItemContent}>
                <Text style={[mbLocal.sheetItemTitle, isDark && { color: colors.text.primary }]}>Edit</Text>
                <Text style={[mbLocal.sheetItemSub, isDark && { color: colors.text.secondary }]}>Available on desktop only</Text>
              </View>
            </Pressable>
            <View style={[mbLocal.sheetDivider, isDark && { backgroundColor: colors.border.divider }]} />

            {/* View Delivery Details */}
            <Pressable
              style={({ pressed }) => [mbLocal.sheetItem, pressed && mbLocal.sheetItemPressed, isDark && pressed && { backgroundColor: colors.background.elevated }]}
              onPress={() => {
                setMobileActionSheet(null);
                showMobileSnackbar("Delivery details (not wired)");
              }}
            >
              <View style={[mbLocal.sheetIconWrap, { backgroundColor: isDark ? "#0d2a44" : "#EFF6FF" }]}>
                <MaterialIcons name="bar-chart" size={22} color={isDark ? colors.brand.primary : "#034895"} />
              </View>
              <View style={mbLocal.sheetItemContent}>
                <Text style={[mbLocal.sheetItemTitle, isDark && { color: colors.text.primary }]}>View Delivery Details</Text>
                <Text style={[mbLocal.sheetItemSub, isDark && { color: colors.text.secondary }]}>Recipients, seen rate, and audit log</Text>
              </View>
            </Pressable>
            </View>
          </View>
        </Modal>

        {/* ── Snackbar (black, white text, slides from top) ──── */}
        {mobileSnackbar ? (
          <Animated.View
            style={[mbLocal.snackbarWrap, { transform: [{ translateY: snackbarAnim }] }]}
            pointerEvents="none"
          >
            <View style={[mbLocal.snackbar, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <MaterialIcons name="check" size={16} color="#fff" style={{ marginRight: 8 }} />
              <Text style={mbLocal.snackbarText}>{mobileSnackbar}</Text>
            </View>
          </Animated.View>
        ) : null}

        {/* ── Header ───────────────────────────────────────────── */}
        <View style={[mobileStyles.headerBlock, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider }]}>
          <View style={mobileStyles.headerLeft}>
            <Pressable style={mobileStyles.backArrowWrap} onPress={() => onNavigate("Ticketing Portal")}>
              <MaterialIcons name="arrow-back" size={24} color={isDark ? colors.text.primary : "#1a2332"} />
            </Pressable>
            <View style={mobileStyles.headerTitleBlock}>
              <Text style={[mobileStyles.headerTitle, isDark && { color: colors.text.primary }]}>Notifications</Text>
              <Text style={[mobileStyles.headerSubtitle, isDark && { color: colors.text.secondary }]}>View and deactivate. Create on desktop.</Text>
            </View>
          </View>
          <Pressable
            style={mobileStyles.addIconBtn}
            onPress={() => showMobileSnackbar("Notification creation is desktop only")}
          >
            <MaterialIcons name="add" size={26} color={isDark ? colors.text.primary : "#1a2332"} />
          </Pressable>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 32 }} showsVerticalScrollIndicator={false}>
          {SECTIONS.map(({ key, label }) => {
            const sectionNotifs = notifications.filter((n) => n.statusTab === key);
            return (
              <View key={key}>
                <View style={mobileStyles.sectionLabelRow}>
                  <Text style={[mobileStyles.sectionLabel, isDark && { color: colors.text.secondary }]}>
                    {label} ({sectionNotifs.length})
                  </Text>
                </View>
                <View style={mobileStyles.cardList}>
                  {sectionNotifs.length > 0
                    ? sectionNotifs.map(renderCard)
                    : <Text style={{ color: isDark ? colors.text.secondary : "#818181", fontSize: 13, marginBottom: 8 }}>None</Text>
                  }
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    );
  }

  /* ── Web / iPad layout ───────────────────────── */
  return (
    <View style={[styles.screenRoot, isDark && { backgroundColor: "#141414" }]}>
      <View style={styles.container}>
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* ── Page header ──────────────────────────────────────────── */}
          <View style={styles.pageHeader}>
            <View style={styles.pageTitleBlock}>
              <View style={styles.breadcrumbRow}>
                <Pressable onPress={() => onNavigate("Ticketing Portal")}>
                  <Text style={[styles.breadcrumbLink, isDark && { color: "#B0B0B0" }]}>Ticketing Portal</Text>
                </Pressable>
                <Text style={[styles.breadcrumbSeparator, isDark && { color: "#6E6E6E" }]}>/</Text>
                <Text style={[styles.breadcrumbCurrent, isDark && { color: "#F2F2F2" }]}>Notifications &amp; Comms</Text>
              </View>
              <Text style={[styles.pageTitle, isDark && { color: "#F2F2F2" }]}>Notifications &amp; Communications</Text>
              <Text style={[styles.pageSubtitle, isDark && { color: "#B0B0B0" }]}>
                Create targeted notifications, manage homepage banners, and measure delivery.
              </Text>
            </View>

            <View style={styles.pageActions}>
              <Pressable
                style={({ pressed }) => [
                  styles.btnPrimary,
                  isDark && { backgroundColor: colors.brand.primary },
                  !hasWriteAccess && styles.btnDisabled,
                  pressed && hasWriteAccess ? styles.btnPrimaryPressed : null,
                ]}
                onPress={hasWriteAccess ? handleCreateNotification : undefined}
                disabled={!hasWriteAccess}
              >
                <MaterialIcons name="add" size={15} color="#ffffff" />
                <Text style={styles.btnPrimaryText}>Create Notification</Text>
              </Pressable>
            </View>
          </View>

          {/* Read-Only Banner for Super Users */}
          {isReadOnly && (
            <View style={{
              backgroundColor: isDark ? "#2B1A00" : "#FFF4E5",
              borderLeftWidth: 4,
              borderLeftColor: "#FF8C09",
              paddingHorizontal: 16,
              paddingVertical: 12,
              marginBottom: 16,
              borderRadius: 6,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}>
              <MaterialIcons name="visibility" size={20} color="#FF8C09" />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: isDark ? "#F2F2F2" : "#2d2d2d", marginBottom: 2 }}>
                  Read-Only Access
                </Text>
                <Text style={{ fontSize: 13, color: isDark ? "#B0B0B0" : "#5a5a5a" }}>
                  You have view-only access to this page. Contact your administrator to request edit permissions.
                </Text>
              </View>
            </View>
          )}

          {/* ── Full-width notification list ─────────────────────────── */}
          <NotificationList
            notifications={notifications.filter((n) => n.statusTab === activeTab)}
            activeTab={activeTab}
            tabCounts={tabCounts}
            tabs={tabs}
            onTabChange={setActiveTab}
            onToggleRead={toggleReadStatus}
            onDelete={hasWriteAccess ? handleDelete : undefined}
            onPressLinkedTicket={handlePressLinkedTicket}
            onEdit={hasWriteAccess ? handleEditNotification : undefined}
            isDark={isDark}
            colors={colors}
          />

        </ScrollView>
      </View>

      <CreateNotificationModal
        visible={createModalOpen}
        initialValues={editNotif?.initialValues}
        onClose={closeModal}
        onDraft={handleModalDraft}
        onSend={handleModalSend}
        isDark={isDark}
        colors={colors}
      />
    </View>
  );
};

/* ── Mobile-only supplemental styles ────────────────────────────────────── */
const mbLocal = StyleSheet.create({
  // Deactivated card overlay — subtle, still readable
  cardDeactivated: { opacity: 0.72 },
  dimText:         { color: "#6B7280" },
  dimBtn:          { opacity: 0.4 },
  dimBadge:        { opacity: 0.65 },

  // Status badge (DEACTIVATED / SCHEDULED / SENT / DRAFT) inside stats row
  statusBadge:     { flexDirection: "row", alignItems: "center", gap: 5, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 100, marginLeft: "auto" as any },
  statusBadgeDot:  { width: 7, height: 7, borderRadius: 4 },
  statusBadgeText: { fontSize: 11, fontWeight: "700" },

  // Bottom action sheet
  sheetBackdrop:    { flex: 1, backgroundColor: "rgba(0,0,0,0.45)" },
  sheetWrapper:     { flex: 1, justifyContent: "flex-end", backgroundColor: "rgba(0,0,0,0.45)" },
  sheetContainer:   { backgroundColor: "#ffffff", borderTopLeftRadius: 28, borderTopRightRadius: 28, overflow: "hidden", paddingBottom: 36, paddingTop: 12 },
  sheetHandle:      { width: 40, height: 4, borderRadius: 2, backgroundColor: "#D1D5DB", alignSelf: "center", marginBottom: 16 },
  sheetTitle:       { fontSize: 17, fontWeight: "700", color: "#111827", paddingHorizontal: 20, marginBottom: 12 },
  sheetDivider:     { height: StyleSheet.hairlineWidth, backgroundColor: "#E5E7EB" },
  sheetItem:        { flexDirection: "row", alignItems: "center", paddingHorizontal: 20, paddingVertical: 18, gap: 14 },
  sheetItemPressed: { backgroundColor: "#F9FAFB" },
  sheetIconWrap:    { width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6", alignItems: "center", justifyContent: "center" },
  sheetItemContent: { flex: 1 },
  sheetItemTitle:   { fontSize: 16, fontWeight: "600", color: "#111827" },
  sheetItemSub:     { fontSize: 13, color: "#6b7280", marginTop: 2 },

  // Snackbar (black bg, white text, full-width from top)
  snackbarWrap:     { position: "absolute", top: 0, left: 0, right: 0, zIndex: 9999 },
  snackbar:         { flexDirection: "row", alignItems: "center", backgroundColor: "#1a1a1a", borderRadius: 10, borderWidth: 1.5, borderColor: "#e5e7eb", paddingHorizontal: 18, paddingVertical: 14, shadowColor: "#000", shadowOpacity: 0.3, shadowRadius: 8, shadowOffset: { width: 0, height: 4 }, elevation: 14, marginTop: 12, marginLeft: 16, marginRight: 16 },
  snackbarText:     { fontSize: 14, fontWeight: "600", color: "#ffffff" },
});

export default NotificationsCommsScreen;
